import React, { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { dumpTenant, exportTenant } from '../api/dataExchange';

const CONFIRM_WORD = 'DELETE';

/**
 * Modale de suppression d'entreprise — garde-fou en deux verrous :
 *  1. Télécharger les deux sauvegardes : JSON (complète, restaurable) ET Excel
 *     (lisible par le client). La case de confirmation ne s'active qu'après les
 *     deux téléchargements réussis.
 *  2. Cocher la case « sauvegardes téléchargées » ET saisir « DELETE ».
 * Le bouton de suppression reste désactivé tant que les deux verrous ne sont
 * pas levés. La suppression elle-même est déléguée au parent (onConfirm).
 */
export default function DeleteTenantModal({ tenant, onClose, onConfirm, addToast }) {
  const { t } = useTranslation();
  const [jsonDone, setJsonDone] = useState(false);
  const [excelDone, setExcelDone] = useState(false);
  const [jsonBusy, setJsonBusy] = useState(false);
  const [excelBusy, setExcelBusy] = useState(false);
  const [acknowledged, setAcknowledged] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);

  const downloaded = jsonDone && excelDone;
  const canDelete = downloaded && acknowledged && confirmText === CONFIRM_WORD;

  async function handleDownloadJson() {
    setJsonBusy(true);
    try {
      await dumpTenant(tenant.id, tenant.name);
      setJsonDone(true);
      addToast && addToast(t('settings:deleteModal.jsonDone'));
    } catch (err) {
      addToast && addToast(t('settings:deleteModal.jsonFail', { message: err.message }), 'error');
    } finally {
      setJsonBusy(false);
    }
  }

  async function handleDownloadExcel() {
    setExcelBusy(true);
    try {
      await exportTenant(tenant.id);
      setExcelDone(true);
      addToast && addToast(t('settings:deleteModal.excelDone'));
    } catch (err) {
      addToast && addToast(t('settings:deleteModal.excelFail', { message: err.message }), 'error');
    } finally {
      setExcelBusy(false);
    }
  }

  async function handleConfirm() {
    if (!canDelete) return;
    setDeleting(true);
    await onConfirm();
    // Le parent ferme la modale et navigue ; rien à faire ici en cas de succès.
    setDeleting(false);
  }

  return (
    <div className="settings-modal-overlay">
      <div className="settings-modal" onClick={e => e.stopPropagation()}>
        <div className="settings-modal-header">
          <span className="settings-modal-title">{t('settings:deleteModal.title', { name: tenant.name })}</span>
          <button className="settings-modal-close" onClick={onClose}>✕</button>
        </div>

        <div className="settings-modal-body">
          <p className="settings-modal-warn">
            <Trans
              i18nKey="settings:deleteModal.warn"
              components={{ strong: <strong /> }}
            />
          </p>

          <div className="settings-modal-step">
            <div className="settings-modal-step-num">1</div>
            <div className="settings-modal-step-body">
              <div className="settings-modal-step-lbl">{t('settings:deleteModal.step1')}</div>
              <div className="settings-modal-dl">
                <button
                  type="button"
                  className="settings-btn settings-btn--primary"
                  onClick={handleDownloadJson}
                  disabled={jsonBusy}
                >
                  {jsonBusy
                    ? t('settings:deleteModal.preparing')
                    : jsonDone
                      ? t('settings:deleteModal.redownloadJson')
                      : t('settings:deleteModal.downloadJson')}
                </button>
                {jsonDone && <span className="settings-modal-ok">✓</span>}
              </div>
              <div className="settings-modal-dl">
                <button
                  type="button"
                  className="settings-btn settings-btn--primary"
                  onClick={handleDownloadExcel}
                  disabled={excelBusy}
                >
                  {excelBusy
                    ? t('settings:deleteModal.preparing')
                    : excelDone
                      ? t('settings:deleteModal.redownloadExcel')
                      : t('settings:deleteModal.downloadExcel')}
                </button>
                {excelDone && <span className="settings-modal-ok">✓</span>}
              </div>
            </div>
          </div>

          <div className="settings-modal-step">
            <div className="settings-modal-step-num">2</div>
            <div className="settings-modal-step-body">
              <label className={`settings-modal-check ${downloaded ? '' : 'is-disabled'}`}>
                <input
                  type="checkbox"
                  checked={acknowledged}
                  disabled={!downloaded}
                  onChange={e => setAcknowledged(e.target.checked)}
                />
                {t('settings:deleteModal.ack')}
              </label>

              <label className="settings-modal-field">
                <span className="settings-modal-field-lbl">
                  <Trans
                    i18nKey="settings:deleteModal.confirmLabel"
                    values={{ word: CONFIRM_WORD }}
                    components={{ strong: <strong /> }}
                  />
                </span>
                <input
                  type="text"
                  className="settings-input"
                  value={confirmText}
                  onChange={e => setConfirmText(e.target.value)}
                  placeholder={CONFIRM_WORD}
                  autoComplete="off"
                />
              </label>
            </div>
          </div>
        </div>

        <div className="settings-modal-footer">
          <button type="button" className="settings-btn settings-btn--ghost" onClick={onClose}>
            {t('settings:deleteModal.cancel')}
          </button>
          <button
            type="button"
            className="settings-btn settings-btn--danger"
            onClick={handleConfirm}
            disabled={!canDelete || deleting}
          >
            {deleting ? t('settings:deleteModal.deleting') : t('settings:deleteModal.delete')}
          </button>
        </div>
      </div>
    </div>
  );
}
